import { AppStrings } from "../../../utils/constants/AppStrings";
import { stringField } from "./parseFields";

export type ReportingRun = {
  runId: string;
  lastModified: string;
  pack: string;
  brand: string;
  referenceDate: string;
  status: string;
};

export type ReportingRunFile = {
  key: string;
  grouping: string;
  fileName: string;
};

export type ReportingRunDetail = {
  runId: string;
  pack: string;
  brand: string;
  referenceDate: string;
  status: string;
  error: string;
  files: ReportingRunFile[];
};

export type ReportingFileGroup = {
  grouping: string;
  label: string;
  files: ReportingRunFile[];
};

const GROUPING_LABELS: Record<string, string> = {
  gli: AppStrings.PAM_REGULATOR_PORTAL_GROUPING_GLI,
  brazil: AppStrings.PAM_REGULATOR_PORTAL_GROUPING_BRAZIL,
  peru: AppStrings.PAM_REGULATOR_PORTAL_GROUPING_PERU,
  zip: AppStrings.PAM_REGULATOR_PORTAL_GROUPING_ZIP,
};

export const reportingGroupingLabel = (grouping: string): string =>
  GROUPING_LABELS[grouping] ?? AppStrings.PAM_REGULATOR_PORTAL_GROUPING_OTHER;

export const reportingFileName = (key: string): string => {
  const trimmed = key.trim();
  if (!trimmed) return "";
  const parts = trimmed.split("/").filter(Boolean);
  return parts[parts.length - 1] ?? trimmed;
};

const asRecord = (value: unknown): Record<string, unknown> | null => {
  if (value == null || typeof value !== "object" || Array.isArray(value)) {
    return null;
  }
  return value as Record<string, unknown>;
};

const unwrapPayload = (response: unknown): Record<string, unknown> => {
  const root = asRecord(response);
  if (!root) return {};
  const nested = asRecord(root.data);
  return nested ?? root;
};

const parseRun = (value: unknown): ReportingRun | null => {
  const record = asRecord(value);
  if (!record) return null;
  const runId = stringField(record, "run_id", "runId", "id");
  if (!runId) return null;
  return {
    runId,
    lastModified: stringField(record, "last_modified", "lastModified"),
    pack: stringField(record, "pack"),
    brand: stringField(record, "brand"),
    referenceDate: stringField(record, "reference_date", "referenceDate"),
    status: stringField(record, "status"),
  };
};

const parseFile = (value: unknown): ReportingRunFile | null => {
  const record = asRecord(value);
  if (!record) return null;
  const key = stringField(record, "key");
  if (!key) return null;
  const grouping = stringField(record, "grouping") || "other";
  return {
    key,
    grouping,
    fileName: reportingFileName(key),
  };
};

export const parseReportingRunList = (response: unknown): ReportingRun[] => {
  const payload = unwrapPayload(response);
  const raw = payload.runs;
  if (!Array.isArray(raw)) return [];
  return raw
    .map(parseRun)
    .filter((row): row is ReportingRun => row !== null);
};

export const parseReportingRunDetail = (
  response: unknown,
): ReportingRunDetail | null => {
  const payload = unwrapPayload(response);
  const runId = stringField(payload, "run_id", "runId", "id");
  if (!runId) return null;
  const files = Array.isArray(payload.files)
    ? payload.files
        .map(parseFile)
        .filter((file): file is ReportingRunFile => file !== null)
    : [];
  return {
    runId,
    pack: stringField(payload, "pack"),
    brand: stringField(payload, "brand"),
    referenceDate: stringField(payload, "reference_date", "referenceDate"),
    status: stringField(payload, "status"),
    error: stringField(payload, "error"),
    files,
  };
};

export const parseReportingStartedRunId = (response: unknown): string | null => {
  const payload = unwrapPayload(response);
  return stringField(payload, "run_id", "runId", "id") || null;
};

export const groupReportingFiles = (
  files: ReportingRunFile[],
): ReportingFileGroup[] => {
  const order: string[] = [];
  const byGrouping = new Map<string, ReportingRunFile[]>();
  for (const file of files) {
    const grouping = file.grouping || "other";
    if (!byGrouping.has(grouping)) {
      order.push(grouping);
      byGrouping.set(grouping, []);
    }
    byGrouping.get(grouping)?.push(file);
  }
  return order.map((grouping) => ({
    grouping,
    label: reportingGroupingLabel(grouping),
    files: byGrouping.get(grouping) ?? [],
  }));
};
